/**
 * Shared Sentry configuration for both client and server
 */

/**
 * Local mirror of `@sentry/core`'s `TracesSamplerSamplingContext`. Not
 * imported directly: `@sentry/core` isn't resolvable from this package under
 * pnpm, and `@sentry/sveltekit`'s merged type barrel drops this name as
 * ambiguous. Keep in sync with `samplingcontext.d.ts` if the SDK changes.
 */
export interface SentryTracesSamplerContext {
	name: string;
	parentSampled?: boolean;
	parentSampleRate?: number;
	attributes?: Record<string, unknown>;
}

export const SENTRY_DSN =
	'https://301f36138b8fb5eadb575346186d3219@o968153.ingest.us.sentry.io/4507946504421376';

export const IGNORE_ERRORS = [
	/The email or password you have entered is incorrect/i,
	/Sorry, an unknown error occurred\. Please try again later\./i,
];

/** Anchored to the host boundary so it covers every abcmouse.com subdomain (the old regex only matched two, silently dropping staging/review errors). */
export const ABCMOUSE_ALLOW_URLS = [/^https?:\/\/(?:[a-z0-9-]+\.)*abcmouse\.com(?:[/?#]|$)/];

/**
 * Fetch/XHR destinations that get `sentry-trace`/`baggage` headers attached.
 * Same-origin API paths ONLY (verified against src/lib/services/api/index.js;
 * ApiService.BASE_URL is ''). The SDK default (`/^\//`) matches every
 * same-origin request, leaking trace headers into /metrics/ (the GTM proxy)
 * and /assets-mkt/. Cross-origin service hosts (app.abcmouse.com, api.aofl.com,
 * tools.aofl.com) are deliberately absent: custom headers force a CORS
 * preflight those backends may not allow, and nothing consumes the headers
 * there today - add them only alongside backend Sentry tracing plus a
 * verified Access-Control-Allow-Headers.
 */
export const TRACE_PROPAGATION_TARGETS = [
	/^\/ws\/amsl\/[\d.]+\/json\//,
	/^\/apis\/mws\/[\d.]+\/json\//,
];

/** Patterns used to fingerprint offline / transient fetch failures */
const NETWORK_ERROR_PATTERNS = [
	/fetch-failed: network-failed-to-fetch/i,
	/Failed to fetch/i,
	/load failed/i,
];

const MODULE_ERROR_PATTERNS = [/Importing a module script failed/i];

/**
 * @param {unknown} message - Exception message text
 * @returns {boolean} Whether it matches a network-error pattern
 */
function isNetworkErrorMessage(message: unknown): boolean {
	const text = message == null ? '' : String(message);
	return NETWORK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * @param {unknown} message - Exception message text
 * @returns {boolean} Whether it matches a module-import-error pattern
 */
function isModuleImportErrorMessage(message: unknown): boolean {
	const text = message == null ? '' : String(message);
	return MODULE_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * @param {unknown} error - String, Error-like, or other
 * @returns {string} Text representation
 */
function stringyError(error: unknown): string {
	if (error == null) {
		return '';
	}
	if (typeof error === 'string') {
		return error;
	}
	if (typeof error === 'object' && 'message' in error) {
		const msg = (error as { message?: unknown }).message;
		return msg == null ? '' : String(msg);
	}
	return String(error);
}

/** Fields `beforeSend` reads/writes. Structural (not the SDK's full `Event` type) so a real `Event` stays assignable. */
type SentryEventLike = {
	exception?: { values?: Array<{ value?: unknown; fatal?: boolean }> };
	extra?: Record<string, unknown>;
	fingerprint?: string[];
	tags?: Record<string, unknown>;
};

/**
 * @param {SentryEventLike} event - Sentry event
 * @returns {boolean} Whether the event looks like a network error
 */
function hasNetworkError(event: SentryEventLike): boolean {
	const values = event.exception?.values;
	if (Array.isArray(values)) {
		if (values.some((ex) => isNetworkErrorMessage(ex?.value))) {
			return true;
		}
	}

	const errorText = stringyError(event.extra?.error);
	return errorText !== '' && isNetworkErrorMessage(errorText);
}

/**
 * @param {SentryEventLike} event - Sentry event
 * @returns {boolean} Whether the event looks like a module-import error
 */
function hasModuleImportError(event: SentryEventLike): boolean {
	const values = event.exception?.values;
	if (Array.isArray(values)) {
		if (values.some((ex) => isModuleImportErrorMessage(ex?.value))) {
			return true;
		}
	}

	const errorText = stringyError(event.extra?.error);
	return errorText !== '' && isModuleImportErrorMessage(errorText);
}

/**
 * A failed goto() chunk fetch on /inbound-gateway strands users on the gateway
 * 404 with zero Sentry signal. The isInboundGateway branches in beforeSend let
 * normally-dropped network/module errors through there (tagged
 * route:inbound-gateway) for the ABC2-19766 abandonment investigation.
 * Remove once that investigation concludes.
 * @returns {boolean} Whether we're on the inbound-gateway route (client only)
 */
function isInboundGatewayContext(): boolean {
	return (
		typeof window !== 'undefined' &&
		typeof window.location?.pathname === 'string' &&
		window.location.pathname.includes('/inbound-gateway')
	);
}

/**
 * Shared beforeSend filter. Generic so it type-checks against the SDK's real
 * Event types without widening the return type.
 * @param {SentryEventLike} event - Sentry event
 * @returns {SentryEventLike | null} Event to send, or null to drop
 */
export function beforeSendHandler<T extends SentryEventLike>(event: T): T | null {
	// Temporary inbound-gateway investigation (ABC2-19766) - see block comment above.
	const isInboundGateway = isInboundGatewayContext();

	if (event.exception && Array.isArray(event.exception.values)) {
		const hasExplicitlyFalseFatal = event.exception.values.some(
			(ex) => 'fatal' in ex && ex.fatal === false
		);

		if (hasExplicitlyFalseFatal) {
			return null;
		}
	}

	if (hasNetworkError(event)) {
		if (isInboundGateway) {
			event.fingerprint = ['inbound-gateway', 'network-error'];
			event.tags = { ...event.tags, route: 'inbound-gateway' };
			return event;
		}

		event.fingerprint = ['network-error', 'fetch-failed', 'ignore-network-error'];

		const errorData = event.extra || {};
		if (errorData.offline === true) {
			event.tags = { ...event.tags, network_status: 'offline' };
		}
	}

	if (hasModuleImportError(event)) {
		if (isInboundGateway) {
			event.fingerprint = ['inbound-gateway', 'module-error'];
			event.tags = { ...event.tags, route: 'inbound-gateway' };
			return event;
		}

		event.fingerprint = ['module-error', 'unloading', 'ignore-module-error'];
	}

	if (event.fingerprint && event.fingerprint.some((f) => f.includes('ignore'))) {
		return null;
	}

	return event;
}

/** Per-environment Sentry enablement + trace-sampling + profiling policy. */
export interface EnvironmentPolicy {
	enabled: boolean;
	profilesSampleRate?: number;
	tracesSampleRate?: number;
	tracesSampler?: (ctx: SentryTracesSamplerContext) => number | boolean;
}

/**
 * local_development is off by default (opt in via localStorage.sentry_debug='true');
 * review/staging sample everything (low traffic; review is where new telemetry
 * gets validated); development (web.dev) is throttled - QA automation alone was
 * ~90k transactions/7d at the old flat 0.25 rate. profilesSampleRate is omitted
 * for environments where profiling isn't worth the noise; it silently no-ops
 * without the Document-Policy: js-profiling response header anyway.
 * @param {string} environment - Environment name
 * @returns {EnvironmentPolicy} Enablement + trace-sampling + profiling policy
 */
export function getEnvironmentPolicy(environment: string): EnvironmentPolicy {
	switch (environment) {
		case 'local_development': {
			let debugEnabled = false;
			try {
				debugEnabled =
					typeof localStorage !== 'undefined' && localStorage.getItem('sentry_debug') === 'true';
			} catch {
				// Private browsing / cookies disabled - stay disabled.
			}
			return debugEnabled ? { enabled: true, tracesSampleRate: 1 } : { enabled: false };
		}
		case 'review':
			return { enabled: true, profilesSampleRate: 1, tracesSampleRate: 1 };
		case 'staging':
			return { enabled: true, profilesSampleRate: 1, tracesSampleRate: 1 };
		case 'development':
			return { enabled: true, tracesSampleRate: 0.05 };
		case 'production':
		default:
			return { enabled: true, profilesSampleRate: 0.05, tracesSampler: makeTracesSampler() };
	}
}

const BASE_PATH = '/abc';

/**
 * Normalizes a pathname, SvelteKit route id, or full URL into a
 * classification key for the sample-rate table: strips the `/abc` base and
 * trailing slash, lowercases, and drops `[param]` segments and beyond
 * (`/subscription/offer/[offer_hash]` → `/subscription/offer`). Concrete
 * pathnames keep their segments (`/subscription/offer/abc123` stays as-is);
 * both forms still land in the same tier via prefix matching in
 * {@link sampleRateForRoute}.
 * @param {string} name - Pathname, route id, or full URL
 * @returns {string} Normalized route path ('' for empty input)
 */
export function normalizeRoutePath(name: string): string {
	if (typeof name !== 'string' || name === '') {
		return '';
	}

	let path = name;

	if (/^https?:\/\//i.test(path)) {
		try {
			path = new URL(path).pathname;
		} catch {
			// Malformed URL - fall through and normalize the raw string as-is.
		}
	}

	// Compared case-insensitively (final output is lowercased anyway below),
	// but sliced off the original so we don't have to re-derive casing.
	const lowerPath = path.toLowerCase();
	if (lowerPath === BASE_PATH || lowerPath.startsWith(`${BASE_PATH}/`)) {
		path = path.slice(BASE_PATH.length) || '/';
	}

	if (path.length > 1 && path.endsWith('/')) {
		path = path.slice(0, -1);
	}

	const paramIndex = path.indexOf('[');
	if (paramIndex !== -1) {
		const prefix = path.slice(0, paramIndex);
		const lastSlash = prefix.lastIndexOf('/');
		path = lastSlash >= 0 ? prefix.slice(0, lastSlash) : '';
		if (path === '') {
			path = '/';
		}
	}

	return path.toLowerCase();
}

/** Rate for INP standalone interaction spans (`ui.interaction.*`). */
export const RATES = {
	inp: 0.1,
};

/** Fallback for routes matching no tier, and for unidentifiable span kinds. */
export const DEFAULT_ROUTE_SAMPLE_RATE = { navigation: 0.05, pageload: 0.1 };

// Ordered tiers - first match wins, so NOT alphabetized (funnel > auth > zero).
export const ROUTE_SAMPLE_RATES: {
	navigation: number;
	pageload: number;
	prefixes: string[];
}[] = [
	{
		// Funnel: money / conversion-critical paths - highest fidelity.
		// Pageload and navigation match so gateway redirect counts vs destination
		// arrival counts are comparable without a 2× rate correction.
		// `'/'` is exact-path only (`startsWith('//')` is false for every other route).
		navigation: 0.5,
		pageload: 0.5,
		prefixes: [
			'/',
			'/inbound-gateway',
			'/subscription',
			'/subscription-consume',
			'/create-account',
			'/reactivation',
			'/upgrade',
			'/assessment-purchase',
			'/download-app',
			'/app-choice',
			'/redeem',
			'/aa-offer',
			'/tutor-offer',
			'/claimed-offer',
			'/prospect-register',
		],
	},
	{
		// Login: conversion-adjacent; navigation matches pageload so SPA arrivals
		// are comparable. `/login` does not match `/login-account-selection`
		// (path-boundary check requires `/login/`).
		navigation: 0.25,
		pageload: 0.25,
		prefixes: ['/login'],
	},
	{
		// Auth: important, but higher-volume and lower business risk than funnel.
		navigation: 0.1,
		pageload: 0.25,
		prefixes: [
			'/login-account-selection',
			'/universal',
			'/magic-link-pending',
			'/qr-entry',
			'/link-entry',
			'/password-reset',
			'/forgot-password',
		],
	},
	{
		// Zero: no analytical value (health checks, logout, dead ends).
		navigation: 0,
		pageload: 0,
		prefixes: ['/healthcheck', '/debug', '/logout', '/unsupported-browser', '/terminate'],
	},
];

/**
 * @param {string} path - Output of {@link normalizeRoutePath}
 * @param {'navigation' | 'pageload'} kind - Which rate to read off the matched tier
 * @returns {number} Sample rate (first matching tier wins)
 */
export function sampleRateForRoute(path: string, kind: 'navigation' | 'pageload'): number {
	for (const tier of ROUTE_SAMPLE_RATES) {
		const isMatch = tier.prefixes.some(
			(prefix) => path === prefix || path.startsWith(`${prefix}/`)
		);
		if (isMatch) {
			return tier[kind];
		}
	}
	return DEFAULT_ROUTE_SAMPLE_RATE[kind];
}

const SENTRY_OP_ATTRIBUTE = 'sentry.op';
const SENTRY_ORIGIN_ATTRIBUTE = 'sentry.origin';

/**
 * Builds the production `tracesSampler`. Discriminators (verified against the
 * installed 10.69.0 SDK source):
 * - `ctx` has no `op` field, and pageload/navigation spans never fold `op`
 *   into `attributes` - the reliable pageload-vs-navigation discriminator is
 *   the `attributes['sentry.origin']` PREFIX. The SvelteKit wrapper emits
 *   `auto.pageload.sveltekit` / `auto.navigation.sveltekit` (not `.browser`),
 *   so match on the prefix, never the full string.
 * - `ctx.name` can't distinguish them either: at sampling time
 *   `window.location` already matches the navigation target.
 * - Standalone Web Vital spans (INP) DO set `attributes['sentry.op']`
 *   explicitly, so `sentry.op` works for the `ui.interaction` branch. That
 *   branch runs BEFORE the parentSampled check on purpose: after every route
 *   transaction ends, the SDK writes its sampling decision into the scope's
 *   propagation context, so late INP spans arrive with `parentSampled` set -
 *   honoring it would tie INP volume to the route rate instead of RATES.inp.
 * @returns {(ctx: SentryTracesSamplerContext) => number | boolean} Sentry tracesSampler
 */
export function makeTracesSampler(): (ctx: SentryTracesSamplerContext) => number | boolean {
	return (ctx: SentryTracesSamplerContext): number | boolean => {
		const opValue = ctx.attributes?.[SENTRY_OP_ATTRIBUTE];
		const op = typeof opValue === 'string' ? opValue : '';

		if (op.startsWith('ui.interaction')) {
			return RATES.inp;
		}

		if (ctx.parentSampled !== undefined) {
			return ctx.parentSampled;
		}

		const originValue = ctx.attributes?.[SENTRY_ORIGIN_ATTRIBUTE];
		const origin = typeof originValue === 'string' ? originValue : '';

		if (origin.startsWith('auto.pageload')) {
			const path = typeof window !== 'undefined' ? window.location.pathname : '';
			return sampleRateForRoute(normalizeRoutePath(path), 'pageload');
		}

		if (origin.startsWith('auto.navigation')) {
			return sampleRateForRoute(normalizeRoutePath(ctx.name), 'navigation');
		}

		// Unknown span kind - lowest-fidelity rate rather than guessing.
		return DEFAULT_ROUTE_SAMPLE_RATE.navigation;
	};
}

/**
 * Base Sentry config (client + server), merged with the environment policy.
 * @param {object} options - Options
 * @param {string} options.environment - Environment name
 * @param {string} options.release - Release name
 * @returns {object} Sentry init configuration
 */
export function getSentryConfig({
	environment,
	release,
}: {
	environment: string;
	release: string;
}) {
	return {
		attachStacktrace: true,
		beforeSend: beforeSendHandler,
		dsn: SENTRY_DSN,
		environment,
		ignoreErrors: IGNORE_ERRORS,
		normalizeDepth: 6,
		release,
		...getEnvironmentPolicy(environment),
	};
}
